import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Share2,
  Copy,
  Check,
  Users,
  MessageCircle,
  Mail,
  Phone,
  Instagram,
  Facebook,
  Twitter,
  Send,
  QrCode,
  Link2,
  Sparkles,
  Crown,
  Heart,
  UserPlus,
  ContactIcon,
  Smartphone,
  ChevronRight,
  Trophy,
  Gift,
  Flame,
  Star
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

// Beta invite message templates - culturally relevant for Black & Latino audiences
const INVITE_MESSAGES = {
  default: `🪑✨ You've been SELECTED for the MyHairChair Beta!

I'm inviting you to be one of the first to try the app that's changing the game for our community.

🔥 What is MyHairChair?
We're building the WORLD'S GREATEST booking platform made BY Barbers and Stylists, FOR Barbers and Stylists. Finally, an app that understands our hair, our styles, our culture.

💫 As a Beta Tester you get:
• FREE access to all features
• Your feedback shapes the app
• Early supporter status
• Help empower Black & Brown beauty professionals

Join the movement. Let's build this together! 👑

`,
  stylist: `🪑✨ Yo! You've been SELECTED for MyHairChair Beta!

I know you've got skills, and I want you to be part of something BIG.

MyHairChair is a new booking app built by Barbers and Stylists for Barbers and Stylists like US - no crazy fees, no corporate nonsense. Just a clean way to book clients and grow YOUR business.

💰 What you get as a Beta Tester:
• 100% FREE during beta
• Your input = real changes
• Priority listing when we launch
• Support Black & Brown owned business movement

Stop letting other apps take 25% of your hard work. Join the family! 👑

`,
  client: `🪑✨ You're INVITED to the MyHairChair Beta!

Tired of calling around trying to book your barber? Can't find a stylist who really knows how to work with YOUR hair?

MyHairChair is about to change everything. We're building the ultimate booking app for OUR community.

✨ As a Beta Tester you get:
• Book the best Black & Brown stylists
• No more phone tag
• FREE to use forever
• Help us build something amazing

Your perfect style is just a tap away! 👑

`
};

const BetaInviteSystem = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState("share");
  const [copied, setCopied] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [customMessage, setCustomMessage] = useState(INVITE_MESSAGES.default);
  const [inviteType, setInviteType] = useState("default");
  const [manualInvite, setManualInvite] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [referralStats, setReferralStats] = useState({
    total_referrals: 0,
    total_shares: 0,
    badge: "Starter",
    recent_referrals: []
  });
  const [ambassadorPromo, setAmbassadorPromo] = useState({
    promo_active: true,
    spots_remaining: 10,
    threshold: 50,
    progress_percent: 0,
    is_winner: false
  });
  const [showWinnerCelebration, setShowWinnerCelebration] = useState(false);
  
  // Generate unique referral link
  const referralCode = user?.user_id?.slice(-8) || "BETA2024";
  const baseUrl = window.location.origin;
  const inviteLink = `${baseUrl}/register?ref=${referralCode}&beta=true`;
  const stylistInviteLink = `${baseUrl}/invite-stylist?ref=${referralCode}`;
  
  // Check if Contact Picker API is available (mobile browsers)
  const hasContactPicker = 'contacts' in navigator && 'ContactsManager' in window;
  
  // Check if Web Share API is available
  const hasWebShare = navigator.share !== undefined;

  // Fetch referral stats and ambassador promo status on mount
  useEffect(() => {
    const fetchReferralStats = async () => {
      if (!user?.user_id) return;
      
      const token = localStorage.getItem("token");
      if (!token) return;
      
      try {
        const [statsRes, sharesRes, ambassadorRes] = await Promise.all([
          axios.get(`${API}/referrals/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/referrals/my-shares`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/referrals/my-ambassador-status`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setReferralStats({
          total_referrals: statsRes.data.total_referrals || 0,
          total_shares: sharesRes.data.total_shares || 0,
          badge: statsRes.data.badge || "Starter",
          recent_referrals: statsRes.data.recent_referrals || [],
          bonus_points_earned: statsRes.data.bonus_points_earned || 0
        });
        
        setAmbassadorPromo({
          promo_active: ambassadorRes.data.promo_active,
          spots_remaining: ambassadorRes.data.spots_remaining,
          threshold: ambassadorRes.data.threshold,
          progress_percent: ambassadorRes.data.progress_percent || 0,
          remaining_needed: ambassadorRes.data.remaining_needed || 50,
          is_winner: ambassadorRes.data.is_winner,
          is_ambassador: ambassadorRes.data.is_ambassador
        });
        
        // Check if user just became an ambassador
        if (ambassadorRes.data.is_ambassador && !ambassadorRes.data.is_winner && ambassadorRes.data.promo_active) {
          // Trigger the check to potentially win
          const checkRes = await axios.get(`${API}/referrals/check-ambassador-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (checkRes.data.just_won) {
            setShowWinnerCelebration(true);
            toast.success("🎉 " + checkRes.data.message);
          }
        }
      } catch (error) {
        console.log("Could not fetch referral stats");
      }
    };
    
    fetchReferralStats();
  }, [user?.user_id]);

  useEffect(() => {
    setCustomMessage(INVITE_MESSAGES[inviteType]);
  }, [inviteType]);

  // Track share action
  const trackShare = async (platform) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    
    try {
      const res = await axios.post(`${API}/referrals/track-share`, 
        { platform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferralStats(prev => ({
        ...prev,
        total_shares: res.data.total_shares || prev.total_shares + 1
      }));
    } catch (error) {
      // Silent fail - don't interrupt sharing flow
    }
  };

  // Copy link to clipboard
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link copied! Share it with your people! 🔥");
      trackShare("copy_link");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error("Couldn't copy link");
    }
  };

  // Copy full message with link
  const copyFullMessage = async () => {
    const fullMessage = `${customMessage}\n👉 Join here: ${inviteLink}`;
    try {
      await navigator.clipboard.writeText(fullMessage);
      toast.success("Message copied! Ready to share! 🚀");
      trackShare("copy_message");
    } catch (err) {
      toast.error("Couldn't copy message");
    }
  };

  // Native share (mobile)
  const nativeShare = async () => {
    if (hasWebShare) {
      try {
        await navigator.share({
          title: "Join MyHairChair Beta! 👑",
          text: customMessage,
          url: inviteLink
        });
        toast.success("Shared successfully!");
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink(); // Fallback to copy
        }
      }
    } else {
      copyLink();
    }
  };

  // Pick contacts from phone (mobile only)
  const pickContacts = async () => {
    if (!hasContactPicker) {
      toast.error("Contact picker not available on this device. Try on mobile!");
      return;
    }
    
    try {
      const props = ['name', 'tel', 'email'];
      const opts = { multiple: true };
      const selectedContacts = await navigator.contacts.select(props, opts);
      setContacts(selectedContacts);
      toast.success(`${selectedContacts.length} contacts selected!`);
    } catch (err) {
      toast.error("Couldn't access contacts. Please allow permission.");
    }
  };

  // Share to specific platform
  const shareToSocial = (platform) => {
    const encodedMessage = encodeURIComponent(customMessage);
    const encodedLink = encodeURIComponent(inviteLink);
    
    const urls = {
      whatsapp: `https://wa.me/?text=${encodedMessage}%0A%0A${encodedLink}`,
      sms: `sms:?body=${encodedMessage}%0A%0A${encodedLink}`,
      instagram: `https://www.instagram.com/`, // Instagram doesn't support direct share links
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}&quote=${encodedMessage}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedLink}`,
      email: `mailto:?subject=${encodeURIComponent("You're Invited to MyHairChair Beta! 👑")}&body=${encodedMessage}%0A%0A${encodedLink}`,
      tiktok: `https://www.tiktok.com/`, // TikTok doesn't support direct share
    };
    
    if (platform === 'instagram' || platform === 'tiktok') {
      copyFullMessage();
      toast.success(`Message copied! Now paste it on ${platform === 'instagram' ? 'Instagram' : 'TikTok'}`);
      window.open(urls[platform], '_blank');
    } else {
      window.open(urls[platform], '_blank');
    }
    
    // Track the share
    trackShare(platform);
  };

  // Send invite via backend (SMS/Email)
  const sendInvite = async (method) => {
    setSending(true);
    try {
      const payload = {
        method,
        recipient: method === 'sms' ? manualInvite.phone : manualInvite.email,
        name: manualInvite.name,
        message: customMessage,
        invite_link: inviteLink,
        sender_name: user?.name || "A friend"
      };
      
      await axios.post(`${API}/invites/send`, payload);
      toast.success(`Invite sent to ${manualInvite.name}! 🎉`);
      setManualInvite({ name: "", phone: "", email: "" });
    } catch (error) {
      // Fallback to direct link
      if (method === 'sms') {
        shareToSocial('sms');
      } else {
        shareToSocial('email');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Winner Celebration Overlay */}
      {showWinnerCelebration && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 p-8 rounded-3xl text-center max-w-md mx-4 shadow-2xl">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-black mb-2">CONGRATULATIONS!</h2>
            <p className="text-black/80 mb-4">You&apos;ve become an Ambassador and won:</p>
            <div className="bg-black/20 rounded-xl p-4 mb-6">
              <p className="text-2xl font-bold text-black">1 Month FREE Pro Membership</p>
            </div>
            <Button 
              onClick={() => setShowWinnerCelebration(false)}
              className="bg-black text-amber-400 hover:bg-gray-900 px-8"
            >
              Awesome! 🎉
            </Button>
          </div>
        </div>
      )}

      {/* Ambassador Promotion Banner */}
      {ambassadorPromo.promo_active && !ambassadorPromo.is_winner && (
        <div className="bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border border-amber-500/50 rounded-xl p-4 animate-pulse-slow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
              <Trophy className="w-5 h-5 text-black" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-amber-400">Share to Earn Challenge!</h3>
                <Badge className="bg-red-500 text-white text-xs animate-pulse">
                  {ambassadorPromo.spots_remaining} spots left!
                </Badge>
              </div>
              <p className="text-stone-400 text-xs">First 10 Ambassadors win 1 month FREE Pro</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-stone-400">Your Progress</span>
              <span className="text-amber-400 font-medium">
                {referralStats.total_referrals} / {ambassadorPromo.threshold} referrals
              </span>
            </div>
            <Progress 
              value={ambassadorPromo.progress_percent} 
              className="h-2 bg-stone-700"
            />
            <p className="text-xs text-stone-500 text-center">
              {ambassadorPromo.remaining_needed > 0 
                ? `${ambassadorPromo.remaining_needed} more referrals to become an Ambassador!`
                : "You've reached Ambassador status! 🏆"
              }
            </p>
          </div>
        </div>
      )}

      {/* Winner Badge */}
      {ambassadorPromo.is_winner && (
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-4 text-black text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Trophy className="w-5 h-5" />
            <span className="font-bold">AMBASSADOR WINNER</span>
            <Trophy className="w-5 h-5" />
          </div>
          <p className="text-sm text-black/80">You&apos;ve earned 1 month FREE Pro membership!</p>
        </div>
      )}

      {/* Beta Header */}
      <div className="text-center space-y-3 pb-4 border-b border-stone-700">
        <div className="flex items-center justify-center gap-2">
          <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1">
            🚀 BETA TESTER INVITE
          </Badge>
        </div>
        <h2 className="text-2xl font-bold text-white">
          Grow the Movement 👑
        </h2>
        <p className="text-stone-400 text-sm max-w-md mx-auto">
          Help us build the world&apos;s greatest booking platform for our community. 
          Every invite strengthens our network!
        </p>
      </div>

      {/* Invite Type Selector */}
      <div className="space-y-2">
        <Label className="text-stone-300">Who are you inviting?</Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "default", label: "Anyone", icon: Users },
            { id: "stylist", label: "Stylist/Barber", icon: Crown },
            { id: "client", label: "Client", icon: Heart }
          ].map((type) => (
            <Button
              key={type.id}
              variant={inviteType === type.id ? "default" : "outline"}
              onClick={() => setInviteType(type.id)}
              className={inviteType === type.id 
                ? "bg-amber-500 text-black" 
                : "border-stone-600 text-stone-300 hover:bg-stone-800"
              }
            >
              <type.icon className="w-4 h-4 mr-1" />
              {type.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 bg-stone-800">
          <TabsTrigger value="share" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </TabsTrigger>
          <TabsTrigger value="contacts" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Phone className="w-4 h-4 mr-1" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="direct" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Send className="w-4 h-4 mr-1" />
            Direct
          </TabsTrigger>
        </TabsList>

        {/* Share Tab */}
        <TabsContent value="share" className="space-y-4 mt-4">
          {/* Quick Share Buttons */}
          <div className="grid grid-cols-4 gap-3">
            <Button
              onClick={() => shareToSocial('whatsapp')}
              className="flex-col h-20 bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-6 h-6 mb-1" />
              <span className="text-xs">WhatsApp</span>
            </Button>
            <Button
              onClick={() => shareToSocial('sms')}
              className="flex-col h-20 bg-blue-600 hover:bg-blue-700"
            >
              <Smartphone className="w-6 h-6 mb-1" />
              <span className="text-xs">Text</span>
            </Button>
            <Button
              onClick={() => shareToSocial('instagram')}
              className="flex-col h-20 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500"
            >
              <Instagram className="w-6 h-6 mb-1" />
              <span className="text-xs">Instagram</span>
            </Button>
            <Button
              onClick={() => shareToSocial('facebook')}
              className="flex-col h-20 bg-blue-700 hover:bg-blue-800"
            >
              <Facebook className="w-6 h-6 mb-1" />
              <span className="text-xs">Facebook</span>
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Button
              onClick={() => shareToSocial('twitter')}
              className="flex-col h-20 bg-sky-500 hover:bg-sky-600"
            >
              <Twitter className="w-6 h-6 mb-1" />
              <span className="text-xs">Twitter/X</span>
            </Button>
            <Button
              onClick={() => shareToSocial('tiktok')}
              className="flex-col h-20 bg-black border border-stone-600"
            >
              <span className="text-2xl mb-1">🎵</span>
              <span className="text-xs">TikTok</span>
            </Button>
            <Button
              onClick={() => shareToSocial('email')}
              className="flex-col h-20 bg-stone-700 hover:bg-stone-600"
            >
              <Mail className="w-6 h-6 mb-1" />
              <span className="text-xs">Email</span>
            </Button>
            <Button
              onClick={nativeShare}
              className="flex-col h-20 bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Share2 className="w-6 h-6 mb-1" />
              <span className="text-xs">More</span>
            </Button>
          </div>

          {/* Copy Link Section */}
          <div className="space-y-2 pt-4 border-t border-stone-700">
            <Label className="text-stone-300">Your Unique Beta Link</Label>
            <div className="flex gap-2">
              <Input
                value={inviteLink}
                readOnly
                className="bg-stone-800 border-stone-700 text-white text-sm"
              />
              <Button
                onClick={copyLink}
                className={copied ? "bg-green-600" : "bg-amber-500 hover:bg-amber-600 text-black"}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Message Preview */}
          <div className="space-y-2">
            <Label className="text-stone-300">Invite Message (editable)</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className="bg-stone-800 border-stone-700 text-white min-h-[150px] text-sm"
            />
            <Button
              onClick={copyFullMessage}
              variant="outline"
              className="w-full border-stone-600 text-stone-300"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Full Message + Link
            </Button>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          {hasContactPicker ? (
            <>
              <Button
                onClick={pickContacts}
                className="w-full h-16 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-lg"
              >
                <Phone className="w-6 h-6 mr-3" />
                Select from Phone Contacts
              </Button>
              <p className="text-stone-500 text-xs text-center">
                Your contacts stay on your device - we never store them
              </p>
              
              {contacts.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  <Label className="text-stone-300">Selected Contacts ({contacts.length})</Label>
                  {contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-800 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{contact.name?.[0] || "Unknown"}</p>
                        <p className="text-stone-400 text-sm">{contact.tel?.[0] || contact.email?.[0]}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setManualInvite({
                            name: contact.name?.[0] || "",
                            phone: contact.tel?.[0] || "",
                            email: contact.email?.[0] || ""
                          });
                          setActiveTab("direct");
                        }}
                        className="bg-amber-500 text-black"
                      >
                        Invite
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 space-y-4">
              <Smartphone className="w-16 h-16 mx-auto text-stone-600" />
              <div>
                <p className="text-stone-400">Contact picker works best on mobile</p>
                <p className="text-stone-500 text-sm">Open this page on your phone to select contacts directly</p>
              </div>
              <Button
                onClick={() => setActiveTab("direct")}
                className="bg-amber-500 text-black"
              >
                Enter Contact Manually
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Direct Invite Tab */}
        <TabsContent value="direct" className="space-y-4 mt-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-300">Their Name</Label>
              <Input
                placeholder="e.g., Marcus, Maria"
                value={manualInvite.name}
                onChange={(e) => setManualInvite({...manualInvite, name: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-stone-300">Phone Number</Label>
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={manualInvite.phone}
                onChange={(e) => setManualInvite({...manualInvite, phone: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-stone-300">Email (optional)</Label>
              <Input
                type="email"
                placeholder="their@email.com"
                value={manualInvite.email}
                onChange={(e) => setManualInvite({...manualInvite, email: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                onClick={() => {
                  const smsUrl = `sms:${manualInvite.phone}?body=${encodeURIComponent(customMessage + "\n\n" + inviteLink)}`;
                  window.open(smsUrl);
                }}
                disabled={!manualInvite.phone}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send Text
              </Button>
              <Button
                onClick={() => {
                  const mailUrl = `mailto:${manualInvite.email}?subject=${encodeURIComponent("You're Invited to MyHairChair Beta! 👑")}&body=${encodeURIComponent(customMessage + "\n\n" + inviteLink)}`;
                  window.open(mailUrl);
                }}
                disabled={!manualInvite.email}
                className="bg-stone-700 hover:bg-stone-600"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </div>

            <Button
              onClick={() => shareToSocial('whatsapp')}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Send via WhatsApp
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dedicated Stylist Invite Link */}
      <div className="pt-4 border-t border-stone-700 space-y-3">
        <div className="text-center">
          <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white mb-2">
            🎯 Recruiting Stylists?
          </Badge>
          <p className="text-stone-400 text-sm">Share our dedicated landing page for barbers & stylists</p>
        </div>
        <div className="flex gap-2">
          <Input
            value={stylistInviteLink}
            readOnly
            className="bg-stone-800 border-stone-700 text-white text-xs"
          />
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(stylistInviteLink);
              toast.success("Stylist invite link copied! 💇");
            }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <a 
          href={stylistInviteLink} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block text-center text-amber-400 hover:text-amber-300 text-sm underline"
        >
          Preview the page →
        </a>
      </div>

      {/* Stats */}
      <div className="pt-4 border-t border-stone-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-amber-400">{referralStats.total_shares}</p>
            <p className="text-stone-500 text-xs">Invites Sent</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">{referralStats.total_referrals}</p>
            <p className="text-stone-500 text-xs">Joined</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-purple-400">
              {referralStats.badge === "Ambassador" ? "🏆" : 
               referralStats.badge === "Champion" ? "🥇" :
               referralStats.badge === "Advocate" ? "⭐" :
               referralStats.badge === "Supporter" ? "🌟" : "👑"}
            </p>
            <p className="text-stone-500 text-xs">{referralStats.badge}</p>
          </div>
        </div>
        
        {/* Bonus Points */}
        {referralStats.bonus_points_earned > 0 && (
          <div className="mt-3 text-center">
            <p className="text-sm text-amber-400">
              🎁 You&apos;ve earned <span className="font-bold">{referralStats.bonus_points_earned}</span> bonus loyalty points!
            </p>
          </div>
        )}
        
        {/* Recent Referrals */}
        {referralStats.recent_referrals?.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-stone-400 text-xs font-medium">Recent Sign-ups from Your Invites:</p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {referralStats.recent_referrals.slice(0, 3).map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs bg-stone-800/50 rounded px-2 py-1">
                  <span className="text-white">{ref.referred_name}</span>
                  <Badge className={ref.referred_role === "client" ? "bg-blue-500/20 text-blue-400" : "bg-amber-500/20 text-amber-400"}>
                    {ref.referred_role}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Referral Milestones */}
        <div className="mt-4 pt-3 border-t border-stone-700/50">
          <p className="text-stone-400 text-xs text-center mb-2">Referral Milestones</p>
          <div className="flex justify-between text-xs">
            <div className={`text-center ${referralStats.total_referrals >= 5 ? 'text-amber-400' : 'text-stone-600'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1 ${referralStats.total_referrals >= 5 ? 'bg-amber-500/20' : 'bg-stone-800'}`}>
                {referralStats.total_referrals >= 5 ? '✓' : '5'}
              </div>
              <span>Supporter</span>
            </div>
            <div className={`text-center ${referralStats.total_referrals >= 10 ? 'text-amber-400' : 'text-stone-600'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1 ${referralStats.total_referrals >= 10 ? 'bg-amber-500/20' : 'bg-stone-800'}`}>
                {referralStats.total_referrals >= 10 ? '✓' : '10'}
              </div>
              <span>Advocate</span>
            </div>
            <div className={`text-center ${referralStats.total_referrals >= 25 ? 'text-amber-400' : 'text-stone-600'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1 ${referralStats.total_referrals >= 25 ? 'bg-amber-500/20' : 'bg-stone-800'}`}>
                {referralStats.total_referrals >= 25 ? '✓' : '25'}
              </div>
              <span>Champion</span>
            </div>
            <div className={`text-center ${referralStats.total_referrals >= 50 ? 'text-amber-400' : 'text-stone-600'}`}>
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1 ${referralStats.total_referrals >= 50 ? 'bg-amber-500/20' : 'bg-stone-800'}`}>
                {referralStats.total_referrals >= 50 ? '✓' : '50'}
              </div>
              <span>Ambassador</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaInviteSystem;
